import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'
import { GRACKLE_COLORS } from '@/lib/theme'

export default function GettingStartedSection() {
  return (
    <>
      <section id="overview" aria-labelledby="overview-heading">
        <h3 id="overview-heading" className="font-semibold mb-2">
          Welcome to Grackle CRM
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Grackle CRM is built to help sales teams like yours stay organized, close deals faster,
          and keep every customer relationship on track. Whether you are managing a handful of
          accounts or working through hundreds of leads a week, everything you need is right here —
          accounts, contacts, leads, opportunities, quotes, and the analytics to see how your
          pipeline is performing.
        </p>
        <p className="text-sm text-muted-foreground">
          This guide walks you through the key features so you can hit the ground running. If you
          are brand new, start with the{' '}
          <a
            href="#quick-start"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Quick Start Checklist
          </a>{' '}
          below.
        </p>
      </section>
      <Separator />
      <section id="navigation" aria-labelledby="navigation-heading">
        <h3 id="navigation-heading" className="font-semibold mb-2">
          Finding Your Way Around
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          The sidebar on the left is your main menu. It groups features into sections you can expand
          or collapse:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Activities</span> — Tasks, Events, and Emails
          </li>
          <li>
            <span className="font-medium">CRM</span> — Accounts, Contacts, Leads, and Opportunities
          </li>
          <li>
            <span className="font-medium">Sales</span> — Quotes, Orders, Products, and the Price
            List
          </li>
          <li>
            <span className="font-medium">Analytics</span> — Dashboards, Reports, and the Report
            Builder
          </li>
          <li>
            <span className="font-medium">Support</span> — Ticketing system for customer issues
          </li>
          <li>
            <span className="font-medium">Admin</span> — User management, Settings, and this Help
            page
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          On mobile devices, tap the menu icon in the top-left corner to open the navigation drawer.
        </p>
      </section>
      <Separator />
      <section id="search" aria-labelledby="search-heading">
        <h3 id="search-heading" className="font-semibold mb-2">
          Global Search
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Need to find a record fast? Use the search icon in the top header or press{' '}
          <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted border border-border rounded">
            Ctrl
          </kbd>{' '}
          +{' '}
          <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted border border-border rounded">
            K
          </kbd>{' '}
          to open Global Search. It searches across accounts, contacts, leads, opportunities, tasks,
          events, quotes, and orders all at once.
        </p>
        <ProTip>
          <span className="font-medium">Speed tip:</span> When you are on a call and need to pull up
          a record quickly,{' '}
          <kbd
            className="px-1.5 py-0.5 text-xs font-semibold border rounded"
            style={{
              backgroundColor: GRACKLE_COLORS.pollen,
              borderColor: GRACKLE_COLORS.prussianBlue,
              color: GRACKLE_COLORS.prussianBlue,
            }}
          >
            Ctrl
          </kbd>{' '}
          +{' '}
          <kbd
            className="px-1.5 py-0.5 text-xs font-semibold border rounded"
            style={{
              backgroundColor: GRACKLE_COLORS.pollen,
              borderColor: GRACKLE_COLORS.prussianBlue,
              color: GRACKLE_COLORS.prussianBlue,
            }}
          >
            K
          </kbd>{' '}
          is your best friend. Type a company name or person and jump straight to their record.
        </ProTip>
      </section>
      <Separator />
      <section id="my-view" aria-labelledby="my-view-heading">
        <h3 id="my-view-heading" className="font-semibold mb-2">
          My Records vs. All Records
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Most list pages start by showing only the records assigned to you — your leads, your
          accounts, your tasks. This keeps your view focused when you have a lot on your plate.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Use the <span className="font-medium">My / All</span> toggle at the top of any list to
          switch between your records and everything in the system. This applies to Accounts,
          Contacts, Leads, Opportunities, Tasks, and Events.
        </p>
        <ProTip>
          <span className="font-medium">High-volume tip:</span> Keep the toggle on "My" for your
          daily workflow. Switch to "All" when you need to find unassigned records or check on a
          teammate's pipeline.
        </ProTip>
      </section>
    </>
  )
}
